import { useState, useEffect } from 'react';
import { API, AA_IMPACT_LOGO_URL } from '@/lib/constants';
import { Badge } from '@/components/ui/badge';
import { Flag, MapPin, Calendar, Trophy, Timer, Users, TrendingUp, Zap, Clock } from 'lucide-react';

const TEAM_COLORS = {
  'Mercedes-AMG Petronas': '#00D2BE',
  'Scuderia Ferrari': '#DC0000',
  'McLaren Racing': '#FF8700',
  'Red Bull Racing': '#3671C6',
  'Haas F1 Team': '#B6BABD',
  'Racing Bulls': '#6692FF',
  'Aston Martin': '#006F62',
  'Williams Racing': '#005AFF',
  'Alpine F1 Team': '#0090FF',
  'Stake F1 Team': '#52E252',
  'Sauber': '#52E252',
  'Cadillac F1 Team': '#C0C0C0',
};

function getColor(team) {
  for (const [k, c] of Object.entries(TEAM_COLORS)) {
    if (team?.includes(k) || k.includes(team)) return c;
  }
  return '#6b8299';
}

const SESSION_LABELS = { sprint_qualifying: 'Sprint Qualifying', sprint: 'Sprint', qualifying: 'Qualifying', race: 'Race' };
const SESSION_SHORT = { sprint_qualifying: 'SQ', sprint: 'SPR', qualifying: 'Q', race: 'RACE' };

/* ── Winner Spotlight ── */
function WinnerSpotlight({ driver }) {
  if (!driver) return null;
  const color = getColor(driver.team_name);
  return (
    <div className="relative flex items-center gap-5" data-testid="hero-winner-spotlight">
      <div className="relative">
        <div className="absolute -inset-1.5 rounded-full opacity-25 blur-lg" style={{ backgroundColor: color }} />
        {driver.driver_image ? (
          <img src={driver.driver_image} alt={driver.driver_name} className="relative h-24 w-24 rounded-full border-[3px] object-cover bg-[#0B1E31] sm:h-28 sm:w-28" style={{ borderColor: color }} />
        ) : (
          <div className="relative flex h-24 w-24 items-center justify-center rounded-full border-[3px] bg-[#0B1E31] sm:h-28 sm:w-28" style={{ borderColor: color }}>
            <span className="text-3xl font-black text-white/30">{driver.driver_name?.charAt(0)}</span>
          </div>
        )}
        <div className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full bg-[#D8B45A] text-sm font-black text-[#0a1929] shadow-[0_4px_12px_rgba(216,180,90,0.4)]">P1</div>
      </div>
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#D8B45A]">Race Winner</p>
        <h2 className="text-xl font-black text-white sm:text-2xl" data-testid="hero-winner-name">{driver.driver_name}</h2>
        <p className="mt-0.5 text-sm font-medium" style={{ color }}>{driver.team_name}</p>
        {driver.time ? <p className="mt-1 font-mono text-xs text-white/40">{driver.time}</p> : null}
      </div>
    </div>
  );
}

/* ── Session Result Mini Card ── */
function SessionResultCard({ session, results }) {
  if (!results?.length) return null;
  const isRace = session === 'race';
  return (
    <div
      className={`flex-1 min-w-[160px] snap-start rounded-xl border p-3 ${isRace ? 'border-[#D8B45A]/30 bg-[#D8B45A]/[0.05]' : 'border-white/8 bg-white/[0.02]'}`}
      data-testid={`hero-session-result-${session}`}
    >
      <p className={`mb-2 text-[9px] font-bold uppercase tracking-[0.16em] ${isRace ? 'text-[#D8B45A]' : 'text-white/30'}`}>
        {SESSION_LABELS[session] || session}
      </p>
      <div className="space-y-1.5">
        {results.map((r, i) => {
          const color = getColor(r.team_name);
          const medal = i === 0 ? '#D8B45A' : i === 1 ? '#A8A8A8' : '#CD7F32';
          return (
            <div key={r.position} className="flex items-center gap-2">
              <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[8px] font-black text-white" style={{ backgroundColor: medal }}>
                {r.position}
              </span>
              {r.driver_image ? (
                <img src={r.driver_image} alt="" className="h-6 w-6 flex-shrink-0 rounded-full border object-cover bg-[#0B1E31]" style={{ borderColor: `${color}60` }} />
              ) : null}
              <div className="min-w-0 flex-1">
                <span className="block truncate text-xs font-semibold text-white">{r.driver_name}</span>
                {r.time_or_gap ? <span className="block truncate font-mono text-[9px] text-white/40">{r.time_or_gap}</span> : null}
              </div>
              <div className="flex flex-col items-end gap-0.5">
                <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: color }} />
                {r.fastest_lap ? <span className="text-[7px] font-bold text-[#4ECDC4]">FL</span> : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Championship Bar ── */
function ChampBar({ item, maxPts, index, type }) {
  const color = type === 'driver' ? getColor(item.team_name) : getColor(item.name);
  const pct = maxPts > 0 ? (item.points / maxPts) * 100 : 0;
  const isLeader = index === 0;

  return (
    <div className="flex items-center gap-2" data-testid={`hero-${type}-standing-${item.position}`}>
      <span className={`w-4 text-right font-mono text-[10px] font-bold ${isLeader ? 'text-[#D8B45A]' : index < 3 ? 'text-white/60' : 'text-white/25'}`}>
        {item.position}
      </span>
      <div className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className={`w-24 truncate text-[11px] ${isLeader ? 'font-bold text-white' : 'text-white/60'}`}>{item.name}</span>
      <div className="flex-1">
        <div className="h-4 overflow-hidden rounded-sm bg-white/[0.04]">
          <div
            className="h-full rounded-sm transition-all duration-500"
            style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: color, opacity: isLeader ? 0.9 : 0.5 }}
          />
        </div>
      </div>
      <span className={`w-10 text-right font-mono text-[10px] font-bold tabular-nums ${isLeader ? 'text-[#D8B45A]' : 'text-white/50'}`}>{item.points}</span>
      {item.wins > 0 ? <span className="w-5 rounded bg-[#D8B45A]/15 text-center text-[7px] font-bold text-[#D8B45A]">{item.wins}W</span> : <span className="w-5" />}
    </div>
  );
}

/* ── Progress Bar ── */
function ProgressBar({ completed, total }) {
  const pct = total > 0 ? (completed / total) * 100 : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
        <div className="h-full rounded-full bg-gradient-to-r from-[#2A9D8F] to-[#3498DB] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <span className="font-mono text-xs font-bold text-white">{completed}<span className="text-white/30">/{total}</span></span>
    </div>
  );
}

/* ── Track Preview: What to Expect ── */
function TrackPreview({ data }) {
  const th = data.track_history;
  const nr = data.next_race;
  if (!th) return null;
  const circ = th.circuit || {};
  return (
    <div className="mt-3 rounded-2xl border border-white/[0.06] bg-gradient-to-r from-[#0B1E31] to-[#0D2238]" data-testid="hero-track-preview">
      <div className="flex flex-col lg:flex-row">
        {/* Left: Circuit info */}
        <div className="flex-shrink-0 border-b border-white/[0.06] p-4 lg:w-[220px] lg:border-b-0 lg:border-r">
          <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#3498DB]">Track Preview</p>
          <p className="mt-1 text-sm font-bold text-white" data-testid="track-preview-name">{nr.grand_prix}</p>
          {circ.name ? <p className="mt-0.5 text-[10px] text-white/35">{circ.name}</p> : null}
          <div className="mt-2 space-y-1 text-[10px] text-white/45">
            {circ.laps ? <div className="flex items-center gap-1.5"><Flag className="h-3 w-3 text-white/25" /><span>{circ.laps} laps</span></div> : null}
            <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3 text-white/25" /><span>{nr.location}, {nr.country}</span></div>
          </div>
          {circ.image ? <img src={circ.image} alt={circ.name} className="mt-2 h-16 w-full rounded-lg object-contain opacity-50" /> : null}
        </div>

        {/* Center: Recent Winners */}
        <div className="flex-1 border-b border-white/[0.06] p-4 lg:border-b-0 lg:border-r">
          <p className="mb-2 text-[9px] font-bold uppercase tracking-[0.16em] text-white/30">Recent Winners at {nr.location}</p>
          <div className="space-y-2">
            {th.recent_winners.map((w) => (
              <div key={w.season} className="flex items-center gap-3">
                <span className="w-8 font-mono text-xs font-bold text-white/25">{w.season}</span>
                {w.driver_image ? <img src={w.driver_image} alt={w.driver_abbr} className="h-6 w-6 rounded-full object-cover border border-white/10" /> : null}
                <div className="flex-1 min-w-0">
                  <p className="truncate text-xs font-semibold text-white">{w.driver_name || w.driver_abbr}</p>
                  <p className="text-[10px] text-white/35">{w.team_name}</p>
                </div>
                {w.time ? <span className="font-mono text-[10px] text-white/30">{w.time}</span> : null}
              </div>
            ))}
          </div>
        </div>

        {/* Right: Key Stats */}
        <div className="flex-shrink-0 p-4 lg:w-[280px]">
          <p className="mb-2 text-[9px] font-bold uppercase tracking-[0.16em] text-white/30">What to Expect</p>
          <div className="space-y-2.5">
            {th.dominant_driver ? (
              <div className="flex items-center gap-2.5 rounded-lg bg-[#D8B45A]/[0.06] px-3 py-2">
                <Trophy className="h-4 w-4 flex-shrink-0 text-[#D8B45A]" />
                <div>
                  <p className="text-xs font-bold text-white">{th.dominant_driver.abbr} — {th.dominant_driver.wins} consecutive wins</p>
                  <p className="text-[10px] text-white/35">Most wins at this track (recent)</p>
                </div>
              </div>
            ) : null}
            {th.dominant_team ? (
              <div className="flex items-center gap-2.5 rounded-lg bg-white/[0.03] px-3 py-2">
                <Users className="h-4 w-4 flex-shrink-0 text-[#E67E22]" />
                <div>
                  <p className="text-xs font-semibold text-white">{th.dominant_team.name}</p>
                  <p className="text-[10px] text-white/35">{th.dominant_team.wins} wins from {th.total_races_available} recorded races</p>
                </div>
              </div>
            ) : null}
            {th.fastest_laps?.[0] ? (
              <div className="flex items-center gap-2.5 rounded-lg bg-[#4ECDC4]/[0.06] px-3 py-2">
                <Zap className="h-4 w-4 flex-shrink-0 text-[#4ECDC4]" />
                <div>
                  <p className="text-xs font-semibold text-white">Lap Record: <span className="font-mono text-[#4ECDC4]">{th.fastest_laps[0].time}</span></p>
                  <p className="text-[10px] text-white/35">{th.fastest_laps[0].driver_abbr} ({th.fastest_laps[0].season})</p>
                </div>
              </div>
            ) : null}
            {nr.esg_companies_count > 0 ? (
              <div className="flex items-center gap-2.5 rounded-lg bg-[#2A9D8F]/[0.06] px-3 py-2">
                <Clock className="h-4 w-4 flex-shrink-0 text-[#2A9D8F]" />
                <div>
                  <p className="text-xs font-semibold text-white">{nr.esg_companies_count} ESG companies tracked</p>
                  <p className="text-[10px] text-white/35">{nr.sustainability_theme} · {nr.country} market</p>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Main Hero ── */
export default function HeroBanner({ refreshTick }) {
  const [data, setData] = useState(null);
  const [nextRaceData, setNextRaceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/f1/hero`).then((r) => r.json()),
      fetch(`${API}/f1/next-race`).then((r) => r.json()),
    ])
      .then(([heroData, nrData]) => { setData(heroData); setNextRaceData(nrData); setLoading(false); })
      .catch(() => setLoading(false));
  }, [refreshTick]);

  if (loading) {
    return (
      <div className="mb-6 flex h-48 items-center justify-center rounded-2xl border border-white/10 bg-[rgba(13,34,56,0.92)]" data-testid="hero-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-white/10 border-t-[#D8B45A]" />
      </div>
    );
  }

  if (!data?.featured) return null;

  const { featured, podium, fastest_lap, session_results, drivers, constructors, season_progress } = data;
  const isLive = featured.is_race_window && !featured.completed;
  const winner = podium?.[0];
  const maxDriverPts = drivers?.[0]?.points || 1;
  const maxConstructorPts = constructors?.[0]?.points || 1;
  const driverGap = drivers?.length > 1 ? (drivers[0].points - drivers[1].points).toFixed(0) : '0';
  const constructorGap = constructors?.length > 1 ? (constructors[0].points - constructors[1].points).toFixed(0) : '0';

  const sessionOrder = ['race', 'qualifying', 'sprint', 'sprint_qualifying'];
  const completedSessions = sessionOrder.filter((s) => session_results?.[s]);

  return (
    <div className="mb-6" data-testid="hero-banner">
      {/* ── GP Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-t-2xl border border-b-0 border-white/10 bg-gradient-to-r from-[#0D2238] via-[#15334d] to-[#0D2238] px-5 py-3">
        <div className="flex items-center gap-3">
          {isLive ? (
            <div className="flex items-center gap-2">
              <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E74C3C] opacity-75" /><span className="inline-flex h-2.5 w-2.5 rounded-full bg-[#E74C3C]" /></span>
              <Badge className="border-[#E74C3C]/40 bg-[#E74C3C]/15 text-[#E74C3C] text-[10px] font-bold" data-testid="hero-live-badge">LIVE</Badge>
            </div>
          ) : (
            <Badge className="border-[#2A9D8F]/30 bg-[#2A9D8F]/10 text-[#2A9D8F] text-[10px] font-semibold" data-testid="hero-completed-badge">COMPLETED</Badge>
          )}
          <h1 className="text-lg font-black tracking-tight text-white sm:text-xl" data-testid="hero-gp-name">{featured.grand_prix}</h1>
          <div className="hidden items-center gap-3 text-[10px] text-white/35 sm:flex">
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{featured.country}</span>
            <span className="flex items-center gap-1"><Flag className="h-3 w-3" />{featured.circuit_name}</span>
            <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />R{featured.round_number}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {sessionOrder.map((s) => {
              const done = featured.available_sessions?.includes(s);
              const isCurrent = featured.last_session?.name === s;
              return (
                <span key={s} className={`rounded-md px-2 py-0.5 text-[9px] font-bold tracking-wider ${isCurrent ? 'bg-[#3498DB] text-white shadow-[0_0_8px_rgba(52,152,219,0.3)]' : done ? 'bg-[#2A9D8F]/15 text-[#2A9D8F]' : 'bg-white/[0.04] text-white/20'}`} data-testid={`hero-session-pill-${s}`}>
                  {SESSION_SHORT[s]}{done ? ' \u2713' : ''}
                </span>
              );
            })}
          </div>
          <div className="hidden items-center gap-2 sm:flex" data-testid="hero-brand-mark">
            <img src={AA_IMPACT_LOGO_URL} alt="AA Impact" className="h-6 w-6 rounded-md object-cover opacity-60" />
            <div className="border-l border-white/10 pl-2">
              <p className="text-[8px] font-bold uppercase tracking-[0.14em] text-white/30">AA Impact Inc</p>
              <p className="text-[7px] font-semibold uppercase tracking-[0.12em] text-[#38bdf8]/50">A2 Intelligence</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Session Results Strip ── */}
      {completedSessions.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto scroll-smooth snap-x snap-mandatory border-x border-white/10 bg-[#0a1525] px-4 py-3" data-testid="hero-session-results-strip">
          {completedSessions.map((s) => (
            <SessionResultCard key={s} session={s} results={session_results[s]} />
          ))}
        </div>
      ) : null}

      {/* ── Main Content ── */}
      <div className="rounded-b-2xl border border-t-0 border-white/10 bg-[rgba(13,34,56,0.92)]">
        <div className="grid grid-cols-1 lg:grid-cols-12">

          {/* Left: Winner + Progress */}
          <div className="col-span-1 border-b border-white/5 p-6 lg:col-span-3 lg:border-b-0 lg:border-r" data-testid="hero-podium-section">
            <WinnerSpotlight driver={winner} />

            {podium.length > 1 ? (
              <div className="mt-4 space-y-2 rounded-xl border border-white/5 bg-white/[0.02] p-3">
                {podium.slice(1, 3).map((d, i) => {
                  const color = getColor(d.team_name);
                  const medal = i === 0 ? '#A8A8A8' : '#CD7F32';
                  return (
                    <div key={d.position} className="flex items-center gap-2.5" data-testid={`hero-podium-p${i + 2}`}>
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[9px] font-black text-white" style={{ backgroundColor: medal }}>{i + 2}</span>
                      {d.driver_image ? <img src={d.driver_image} alt="" className="h-8 w-8 rounded-full border object-cover bg-[#0B1E31]" style={{ borderColor: `${color}60` }} /> : null}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-white">{d.driver_name}</p>
                        <p className="text-[9px]" style={{ color }}>{d.team_name?.split(' ')[0]}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {fastest_lap ? (
              <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#4ECDC4]/20 bg-[#4ECDC4]/[0.05] px-3 py-2" data-testid="hero-fastest-lap">
                <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#4ECDC4]">FL</span>
                <span className="text-xs font-semibold text-white">{fastest_lap.driver_name}</span>
                <span className="ml-auto font-mono text-xs font-bold text-[#4ECDC4]">{fastest_lap.lap_time}</span>
              </div>
            ) : null}

            <div className="mt-4">
              <p className="mb-2 text-[9px] uppercase tracking-[0.16em] text-white/25">2026 Season</p>
              <ProgressBar completed={season_progress.completed} total={season_progress.total} />
            </div>

            {/* Next Race Preview */}
            {nextRaceData?.next_race ? (
              <div className="mt-4 rounded-xl border border-[#3498DB]/20 bg-[#3498DB]/[0.04] p-3" data-testid="hero-next-race">
                <div className="flex items-center justify-between">
                  <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[#3498DB]">Next Race</p>
                  <Badge className="border-[#3498DB]/20 bg-[#3498DB]/10 text-[8px] text-[#3498DB]">R{nextRaceData.next_race.round_number}</Badge>
                </div>
                <p className="mt-1 text-sm font-bold text-white" data-testid="next-race-name">{nextRaceData.next_race.grand_prix}</p>
                <div className="mt-1.5 space-y-0.5">
                  <div className="flex items-center gap-1.5 text-[10px] text-white/50">
                    <MapPin className="h-3 w-3 text-white/30" />
                    <span>{nextRaceData.next_race.location}, {nextRaceData.next_race.country}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-white/50">
                    <Calendar className="h-3 w-3 text-white/30" />
                    <span>{nextRaceData.next_race.dates}</span>
                    {nextRaceData.next_race.race_type === 'Sprint' ? <Badge className="ml-1 border-[#E67E22]/20 bg-[#E67E22]/10 text-[7px] text-[#E67E22]">SPRINT</Badge> : null}
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-[9px]">
                  {nextRaceData.next_race.esg_companies_count > 0 ? (
                    <span className="rounded bg-[#2A9D8F]/10 px-1.5 py-0.5 text-[#2A9D8F]">{nextRaceData.next_race.esg_companies_count} ESG companies</span>
                  ) : null}
                  {nextRaceData.next_race.sustainability_theme ? (
                    <span className="rounded bg-white/[0.05] px-1.5 py-0.5 text-white/35">{nextRaceData.next_race.sustainability_theme}</span>
                  ) : null}
                </div>
                {nextRaceData.upcoming?.length > 0 ? (
                  <div className="mt-2.5 border-t border-white/[0.06] pt-2">
                    <p className="mb-1 text-[7px] uppercase tracking-[0.14em] text-white/20">Coming Up</p>
                    <div className="space-y-0.5">
                      {nextRaceData.upcoming.map((r) => (
                        <div key={r.round_number} className="flex items-center gap-2 text-[9px] text-white/30">
                          <span className="w-5 font-mono text-white/20">R{r.round_number}</span>
                          <span className="flex-1 truncate">{r.grand_prix}</span>
                          <span>{r.dates}</span>
                          {r.race_type === 'Sprint' ? <span className="text-[#E67E22]">S</span> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* AA Impact + A2 Intelligence branding */}
            <div className="mt-4 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5" data-testid="hero-brand-strip">
              <div className="flex items-center gap-2.5">
                <img src={AA_IMPACT_LOGO_URL} alt="AA Impact" className="h-7 w-7 rounded-md object-cover opacity-60" />
                <div className="min-w-0">
                  <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-white/35">AA Impact Inc</p>
                  <p className="text-[8px] font-semibold uppercase tracking-[0.12em] text-[#38bdf8]/45">A2 Intelligence</p>
                </div>
              </div>
              <p className="mt-1.5 text-[7px] uppercase tracking-[0.18em] text-white/15">Precision Sustainability Intelligence</p>
            </div>
          </div>

          {/* Center: Driver Championship */}
          <div className="col-span-1 border-b border-white/5 p-5 lg:col-span-5 lg:border-b-0 lg:border-r" data-testid="hero-driver-standings">
            <div className="mb-3 flex items-center justify-between">
              <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">
                <Timer className="h-3 w-3 text-[#3498DB]" /> Driver Championship
                <span className="hidden text-[7px] font-medium tracking-[0.1em] text-[#38bdf8]/25 xl:inline">A2</span>
              </p>
              <div className="flex items-center gap-1.5 rounded-md bg-[#D8B45A]/10 px-2 py-0.5">
                <TrendingUp className="h-3 w-3 text-[#D8B45A]" />
                <span className="text-[10px] font-bold text-[#D8B45A]">+{driverGap}pts lead</span>
              </div>
            </div>
            <div className="space-y-1">
              {drivers.map((d, i) => (
                <ChampBar key={d.position} item={d} maxPts={maxDriverPts} index={i} type="driver" />
              ))}
            </div>
          </div>

          {/* Right: Constructor Championship */}
          <div className="col-span-1 p-5 lg:col-span-4" data-testid="hero-constructor-standings">
            <div className="mb-3 flex items-center justify-between">
              <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">
                <Users className="h-3 w-3 text-[#E67E22]" /> Constructor Championship
                <span className="hidden text-[7px] font-medium tracking-[0.1em] text-[#38bdf8]/25 xl:inline">A2</span>
              </p>
              <div className="flex items-center gap-1.5 rounded-md bg-[#D8B45A]/10 px-2 py-0.5">
                <Trophy className="h-3 w-3 text-[#D8B45A]" />
                <span className="text-[10px] font-bold text-[#D8B45A]">+{constructorGap}pts</span>
              </div>
            </div>
            <div className="space-y-1">
              {constructors.map((c, i) => (
                <ChampBar key={c.position} item={c} maxPts={maxConstructorPts} index={i} type="constructor" />
              ))}
            </div>
          </div>
        </div>

        {/* Track Preview - Historical Stats */}
        {nextRaceData?.track_history ? (
          <TrackPreview data={nextRaceData} />
        ) : null}
      </div>
    </div>
  );
}
